import API_CONFIG from '../config/api';
import { analytics } from './analytics';

// Compression utility functions
export const compressionUtils = {
  /**
   * Compress a file using the API
   * @param {Blob} file - The file to compress
   * @param {string} fileName - Original file name
   * @param {string} compressionLevel - 'low', 'medium', or 'high'
   * @param {string} category - Analytics category (default: 'PDF_Unlocker')
   * @returns {Promise<Blob>} - Compressed file blob
   */
  compressFile: async (file, fileName, compressionLevel, category = 'PDF_Unlocker') => {
    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file, fileName);
      formData.append('type', compressionLevel);

      // Call compression API
      const response = await fetch(API_CONFIG.getPdfCompressUrl(), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Compression failed: ${response.status}`);
      }

      const compressedBlob = await response.blob();
      
      // Track successful compression
      analytics.pdfUnlocker.downloadCompressedPdf(
        fileName, 
        compressionLevel, 
        Math.round(compressedBlob.size / 1024)
      );

      return compressedBlob;
    } catch (error) {
      console.error('Compression error:', error);
      analytics.pdfUnlocker.compressionError(error.message);
      throw error;
    }
  },

  /**
   * Download a file with automatic naming
   * @param {Blob} blob - The file blob to download
   * @param {string} originalName - Original file name
   * @param {string} prefix - Prefix for the downloaded file
   * @param {string} suffix - Suffix for the downloaded file (optional)
   */
  downloadFile: (blob, originalName, prefix = 'processed', suffix = '') => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Create filename with prefix and suffix
    const nameParts = originalName.split('.');
    const extension = nameParts.pop();
    const baseName = nameParts.join('.');
    const fileName = `${prefix}_${baseName}${suffix}.${extension}`;
    
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /**
   * Get human-readable file size
   * @param {number} sizeInBytes - File size in bytes
   * @returns {string} - Formatted file size
   */
  getFileSize: (sizeInBytes) => {
    const sizeInKB = Math.round(sizeInBytes / 1024);
    if (sizeInKB > 1024) {
      return `${(sizeInKB / 1024).toFixed(1)} MB`;
    }
    return `${sizeInKB} KB`;
  },

  /**
   * Compress and download a file in one operation
   * @param {Blob} file - The file to compress
   * @param {string} fileName - Original file name
   * @param {string} compressionLevel - Compression level
   * @param {string} prefix - Download file prefix
   * @returns {Promise<void>}
   */
  compressAndDownload: async (file, fileName, compressionLevel, prefix = 'compressed') => {
    try {
      const compressedBlob = await compressionUtils.compressFile(file, fileName, compressionLevel);
      const suffix = `_${compressionLevel}`;
      compressionUtils.downloadFile(compressedBlob, fileName, prefix, suffix);
      return compressedBlob;
    } catch (error) {
      throw error;
    }
  }
};

export default compressionUtils; 